import {FC, useState} from 'react'
import {PatientsColumn, PatientSmall} from '@/features/patients/types.ts'
import {MenuProvider, MenuButton, MenuButton as AriakitMenuButton} from '@ariakit/react'
import {Button} from '@components/ui/button/Button.tsx'
import {
    DotsHorizontalIcon,
    Download01Icon,
    EyeIcon,
    File04Icon,
    File06Icon,
    FolderIcon,
    PlusIcon,
    Trash01Icon
} from '@components/ui/icon/Icon.tsx'
import {Menu, MenuGroup, MenuItem} from '@components/ui/menu-atoms/MenuAtoms.tsx'
import {Link} from 'react-router-dom'
import {useTranslation} from '@/translations/i18n.tsx'
import {useTheme} from 'styled-components'
import {httpExportPatient} from '@/features/patients/services/patients.http.ts'
import {DeletePatientModal} from '@/features/patients/components/delete-patient-modal/DeletePatientModal.tsx'
import {routes} from '@utilities/constants/routes.tsx'
import {columnVisibilitySearchParamsToPayload, filtersParamToPayload, sortParamToPayload} from '@/utilities/helpers'
import {usePatientsSearchParams} from '../../utils'
import {usePermissions} from '@/features/auth/store/store'
import {ExportModal} from '@/components/commons/export-modal/ExportModal'

export const RowMenu: FC<{patient: PatientSmall; onDeleteSuccess: () => void}> = ({patient, onDeleteSuccess}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const theme = useTheme()
    const {searchParams} = usePatientsSearchParams()
    const [isOpen, setOpen] = useState(false)
    const [isDeletePatientModalOpen, setIsDeletePatientModalOpen] = useState(false)

    const columnVisibility =
        columnVisibilitySearchParamsToPayload(PatientsColumn, searchParams.columnVisibility) ??
        Object.fromEntries(PatientsColumn.options.map(column => [column, true]))

    return (
        <>
            <MenuProvider open={isOpen} setOpen={setOpen}>
                <MenuButton
                    render={
                        <Button
                            className={isOpen ? undefined : 'show-on-hover'}
                            size="xs"
                            shape="square"
                            variant="secondary"
                            onClick={event => event.stopPropagation()}
                        >
                            <DotsHorizontalIcon />
                        </Button>
                    }
                />
                <Menu width="200px" onClick={event => event.stopPropagation()}>
                    <MenuGroup>
                        <MenuItem render={<Link to={patient.id} />}>
                            <EyeIcon />
                            {t('commons:details')}
                        </MenuItem>
                        {permissions?.patients?.write && (
                            <ExportModal
                                entity="patients"
                                trigger={
                                    <MenuItem hideOnClick={false}>
                                        <Download01Icon />
                                        {t('commons:export_option')}
                                    </MenuItem>
                                }
                                fileName="Pazienti"
                                asyncExport
                                promise={({extension, mode}) =>
                                    httpExportPatient({
                                        payload: {
                                            selectedPatientIds: [patient.id],
                                            exceptedPatientIds: [],
                                            type: extension,
                                            filters: filtersParamToPayload(searchParams.filters)?.filters,
                                            fields:
                                                mode == 'view'
                                                    ? PatientsColumn.exclude(['select', 'moreActions']).options.filter(
                                                          column => columnVisibility[column]
                                                      )
                                                    : PatientsColumn.exclude(['select', 'moreActions']).options,
                                            sorts: sortParamToPayload(searchParams.sorts)
                                        }
                                    })
                                }
                                selectedCount={1}
                                allowedExtensions={['xlsx', 'xls', 'csv']}
                            />
                        )}
                        {permissions?.requests?.read ||
                            (permissions?.projects?.read && (
                                <MenuProvider>
                                    <AriakitMenuButton render={<MenuItem />}>
                                        <FolderIcon />
                                        {t('patients:open_in')}
                                    </AriakitMenuButton>
                                    <Menu width="200px" onClick={event => event.stopPropagation()}>
                                        <MenuGroup>
                                            {permissions?.requests?.read && (
                                                <MenuItem
                                                    render={
                                                        <Link
                                                            to={{
                                                                pathname: routes.TREATMENT_REQUESTS_ROUTE.handle.path,
                                                                search: new URLSearchParams({
                                                                    search: patient.fullName
                                                                }).toString()
                                                            }}
                                                        />
                                                    }
                                                >
                                                    <File06Icon />
                                                    {t('commons:treatment_requests')}
                                                </MenuItem>
                                            )}
                                            {permissions?.projects?.read && (
                                                <MenuItem
                                                    render={
                                                        <Link
                                                            to={{
                                                                pathname: routes.PROJECTS_ROUTE.handle.path,
                                                                search: new URLSearchParams({
                                                                    search: patient.fullName
                                                                }).toString()
                                                            }}
                                                        />
                                                    }
                                                >
                                                    <File04Icon />
                                                    {t('commons:rehabilitation_project', {count: 2})}
                                                </MenuItem>
                                            )}
                                        </MenuGroup>
                                    </Menu>
                                </MenuProvider>
                            ))}
                    </MenuGroup>
                    {permissions?.requests?.write && (
                        <MenuGroup>
                            <MenuItem
                                render={
                                    <Link
                                        to={{
                                            pathname: routes.CREATE_TREATMENT_REQUEST.handle.path,
                                            search: `patientId=${patient.id}`
                                        }}
                                    />
                                }
                            >
                                <PlusIcon />
                                {t('patients:create_request')}
                            </MenuItem>
                        </MenuGroup>
                    )}
                    {permissions?.patients?.write && (
                        <MenuGroup>
                            <MenuItem
                                onClick={() => setIsDeletePatientModalOpen(true)}
                                style={{color: theme.palette.danger['600']}}
                            >
                                <Trash01Icon style={{fill: theme.palette.danger['600']}} />
                                {t('commons:delete')}
                            </MenuItem>
                        </MenuGroup>
                    )}
                </Menu>
            </MenuProvider>
            {isDeletePatientModalOpen && (
                <DeletePatientModal
                    patientId={patient.id}
                    patientFullName={patient.fullName}
                    canBeDeleted={patient.canBeDeleted}
                    onClose={() => setIsDeletePatientModalOpen(false)}
                    onSuccess={onDeleteSuccess}
                />
            )}
        </>
    )
}
